// tabs/inventory.ts — client-side port of the PC sheet's Inventory tab
//   options/game/character_sheet_display/inventory/_inventory_inc.php (+ _equipment_slots_inc.php,
//   _inventory_creature_unequipped_column_inc.php, get_container_weight_and_header_inc.php)
//
// Full render-from-blob: header (carry-weight + encumbered badges + search + Add), the equipment
// paper-doll (each equipped item by slot), the on-hand grid, and equipped containers — all from the
// cached caster blob. Tiles reuse window.renderInventoryThumbnail (the canonical drag-drop tile, same
// markup the PHP ft_inventory_tile_mount emits), so drag-drop + search keep working after a TS repaint
// provided the router calls refreshDragAndDrop() + fateTideRefreshInventoryUnequippedSearch() after.
//
// Breakdown charts and inventory totals use the native browser plan. Add Inventory and trade/train
// entry retain their existing form openers; equipment actions use the native commit bridge.
//
// LOOT / PEEK MODES (EC sheet): the encounter-creature inventory tab also opens when the viewer is LOOTING
// another creature — dead/unconscious (auto-loot) or holding a peek-in-bags (pickpocket) status. This pane
// gates drag for the looter and, in peek mode, renders the pickpocket staging zone (1:1 port of
// _inventory_inc.php:2-52). The stage → DC → confirm flow stays in includes/js/pickpocket-init.js (invoked
// via string onclicks); the encounter-map DragManager routes a tile dropped on #pickpocket_staging_zone to
// pickpocketStageItem. Mode is derived from the blob + the viewer caster-id the EC mount passes via
// data-ft-viewer-caster-id, so peek stays correct across caster_update WS repaints (which re-render from the
// cached blob, never re-running PHP).

import type { CachedCaster, CasterItemNode } from '../types';
import { buildEmptyState } from '../../shared/empty-state';
import { helmetState, helmetStateControl, hoodState, hoodStateControl } from '../../shared/helmet-visibility';
import { hoodLookDefault } from '../../../../ft3d/engine/hood-state.js';
import { selectInventoryMeshSet } from '../../../../ft3d/engine/mesh-presentation.js';
import { carryWeightBadgeClasses } from '../read-helpers';
import { inventoryOnHandTotals, inventoryRowTotals, inventoryContents, inventoryDisplayRows, inventoryEquippedSlot, isInventoryDisplayContainer } from '../../janus-engine/display/inventory-totals-21';
import { creatureDisplayAccess } from '../../janus-engine/display/creature-display-access';
import { findInventoryRow, queueInventoryFlag, type Bit } from '../../janus-engine/inventory/flag-adjust';
import { bodyPairsLimbs, isPairedLimb } from '../../shared/paired-limb-forms';
import { fetchDisplayCaster } from '../display-caster-cache';

// Equipment slot UUIDs — mirror of EquipmentSlot::LIST (includes/additions.php), same set the
// inventory bundle's equipment-grid.ts carries. Layout (slots/order/rows) is a display concern.
const SLOT = {
    air_around:    'F2D8C4B3-FA31-291C-995A-2046F84A289F',
    head:          '3805BDD7-3461-DDDB-F445-922A5A992987',
    neck:          '5EDE9876-ED2B-3B93-C683-34428B5FDA5B',
    natural_armor: '3CA73C13-5DC3-0DDF-CAD8-98D1CF573844',
    armor:         '0B279A55-5102-D704-8A57-0C1FF4989DFA',
    back:          'F19F7A27-C0EE-DCFF-BD8F-2FA3CC03F7BB',
    bracers:       'BD255B29-389B-53DC-6116-072B1A8DDD5F',
    clothing:      '3CAA3182-3DA6-F348-CEE3-09803F221466',
    cloak:         '6203F9AE-6699-AB57-B3C7-92864A79520C',
    ring:          '3AD8CEE8-D487-68B1-33F0-CFE04336F9B8',
    breast:        'C48555B1-E374-9AAD-8CA0-C55D07AD6776',
    waist:         'B52059C5-526B-3F8C-E906-0D40FA23A593',
    legs:          'AF43D701-0755-C7E2-D26F-44E6F64AC3E2',
    feet:          '94805078-0B0F-FD3F-77E4-AA6EE4D2E44D',
    main_hand:     'CEF334BF-DE96-1BEA-F72A-A4C98FBF36AD',
    off_hand:      '4FF85144-67EC-9D87-AE28-15137A1A2481',
    both_hands:    'E6D02ACB-D3E9-DD8B-762A-31FE8C8D898D',
    tail:          '16ABAE95-5D18-474D-ADFF-8C20FC2439F1',
} as const;

// natural_weapon_form catalog ids (includes/additions.php NaturalWeaponForm::LIST). A natural weapon
// of either form IS the creature's tail, so it grants the tail equipment slot without anybody having
// to tick the has_tail layer — the owner's rule: "having a natural weapon as a tail automatically
// gives us this equipment slot" (2026-09-02).
const NW_FORM_TAIL = 'FABD5931-2BE5-4A39-BD45-62529717A62F';
const NW_FORM_STINGER = 'FAEFBA34-2176-46E5-911D-51580EF6DA74';

// StatusEffect UUIDs (includes/js/template.js VISUAL_STATUS_EFFECTS / catalogs/StatusEffect.php).
const ST = {
    locked: '477A7977-60D2-4B9C-96B0-6ED4BFD6437E',
    unconscious: 'F73BA661-ADD2-26E0-E265-FBEB7E5E6AF1',
} as const;

// Encumbrance status item ids shown as header badges (literal in _inventory_inc.php).
const BADGE_ENCUMBERED = '306CD53D-AD03-DD13-FDDF-34D9120613CD';
const BADGE_HEAVY = '49F87D1B-3878-789E-D52E-65BA7F2F4218';
const BADGE_OVER = 'F40E19A3-FC86-187F-050E-8C8B69761353';

const UUID_RE = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

type InventoryWindow = Record<string, unknown>;

interface InventoryEntry extends CasterItemNode {
    computed?: {
        cost?: { contents_total_gp?: unknown; total_gp?: unknown };
        weight?: { gross?: unknown; held?: unknown; total?: unknown };
    };
    inventory_in_container?: unknown;
    status_caster_id?: unknown;
}

function recordOf(value: unknown): Record<string, unknown> | null {
    return value !== null && typeof value === 'object' && !Array.isArray(value)
        ? value as Record<string, unknown>
        : null;
}

const inventoryRows = (value: unknown): InventoryEntry[] => inventoryDisplayRows(value) as InventoryEntry[];

function itemIdOf(value: unknown): string {
    if (typeof value === 'string') return value;
    const entry = recordOf(value);
    const items = recordOf(entry?.items);
    return String(items?.item_id ?? entry?.item_id ?? '');
}

function invCall(name: string, ...args: unknown[]): unknown {
    const fn = (window as unknown as InventoryWindow)[name];
    return typeof fn === 'function' ? fn(...args) : undefined;
}

function invActionAttrs(action: string, attrs: Record<string, string> = {}): string {
    const pairs = [`data-ft-inv-action="${escapeHtml(action)}"`];
    for (const [key, value] of Object.entries(attrs)) {
        // HTML lowercases attribute names, so `data-ft-itemId` reads back as dataset.ftItemid,
        // NOT dataset.ftItemId. Kebab-case the key so the camelCase dataset readers resolve.
        pairs.push(`data-ft-${key.replace(/[A-Z]/g, (m) => `-${m.toLowerCase()}`)}="${escapeHtml(value)}"`);
    }
    return pairs.join(' ');
}

function invInfoAttrs(itemId: string): string {
    return invActionAttrs('open-info', { itemId });
}

// ── WARES (BG3 mark-to-sell) ────────────────────────────────────────────────────────────────
// Per-PC "hide wares" view state survives websocket repaints in the module map and browser
// restarts in localStorage; the renderer stamps it as data-ft-inventory-hide-wares on the
// search root, which the filter-search-init display loop ANDs with search + type filters.
const hideWaresByEc = new Map<string, boolean>();

function hideWaresStorageKey(ecIdStr: string): string {
    return 'ft_hide_wares_' + ecIdStr;
}

function isHideWaresOn(ecIdStr: string): boolean {
    if (!hideWaresByEc.has(ecIdStr)) {
        let stored = false;
        try { stored = window.localStorage?.getItem(hideWaresStorageKey(ecIdStr)) === '1'; } catch { /* storage unavailable */ }
        hideWaresByEc.set(ecIdStr, stored);
    }
    return hideWaresByEc.get(ecIdStr) === true;
}

function setHideWares(ecIdStr: string, on: boolean): void {
    hideWaresByEc.set(ecIdStr, on);
    try { window.localStorage?.setItem(hideWaresStorageKey(ecIdStr), on ? '1' : '0'); } catch { /* storage unavailable */ }
}

function isPcCaster(caster: CachedCaster): boolean {
    const pc = (caster as { player_characters?: unknown }).player_characters;
    return !!pc && typeof pc === 'object' && Object.keys(pc as Record<string, unknown>).length > 0;
}

/** Wares affordance gate — deliberately NOT canDrag (that OR includes GM / corpse-loot /
 *  unconscious / peek, none of which may flag another creature's items as their wares). */
function waresToggleEnabled(caster: CachedCaster): boolean {
    return ftHasPlayerPermission(caster) && isPcCaster(caster);
}

/** Per-tile toggle generation — see the rollback guard in toggleWares(). */
const waresRequestSeq = new Map<string, number>();

/**
 * Paint the wares markers onto every rendered copy of this tile RIGHT NOW.
 *
 * Why this exists: the optimistic cache patch is NOT enough on its own. Measured on staging
 * 2026-07-20 — call ftApplyInventoryEditToCache with NO request in flight and it writes
 * inventory_wares into the cached node and returns true, but the repaint it fires
 * (ftRepaintCasterFromCache) does not re-render the sheet inventory grid for this field: the tile
 * still read data-is_wares="0" with no .ft-inv-wares at 300ms, 1s AND 3s. The only thing that ever
 * repainted it was the websocket caster_update ~1.5s after the click — exactly the lag the owner
 * reported ("still waiting on back end to confirm before it changes via the encounterArr push").
 * Wares is a non-critical flag we assume succeeds, so paint it locally; the WS reconcile then
 * lands as a visual no-op, and a rejected write repaints the other way via the rollback below.
 *
 * Markup mirrors renderInventoryThumbnail (includes/js-src/inventory/thumbnail.ts) so the WS
 * re-render produces an identical tile and nothing visibly moves when it arrives.
 */
function paintWaresTile(invId: string, isWares: boolean): void {
    const sel = `.inventory-item[data-inventory_id="${invId.replace(/["\\]/g, '\\$&')}"]`;
    document.querySelectorAll<HTMLElement>(sel).forEach((tile) => {
        tile.setAttribute('data-is_wares', isWares ? '1' : '0');
        tile.classList.toggle('ft-inv-wares', isWares);
        const badge = tile.querySelector('.ft-inv-wares-badge');
        if (isWares && !badge) {
            // Bottom-LEFT, matching thumbnail.ts (top-right = enchant/slot, bottom-right = qty).
            const host = tile.querySelector('.inventory-item-overlay')
                ?? tile.querySelector('.position-absolute.top-0.start-0')
                ?? tile.firstElementChild;
            if (host) {
                const span = document.createElement('span');
                span.className = 'ft-inv-wares-badge position-absolute';
                span.style.cssText = 'bottom:-2px;left:0;z-index:3;pointer-events:none;';
                span.innerHTML = iconMap('merchant', 15, 15);
                host.appendChild(span);
            }
        } else if (!isWares && badge) {
            badge.remove();
        }
    });
}

/**
 * Wares toggles the user has made that the server has not yet echoed back, as invId -> desired 0/1.
 *
 * Why: a caster_update carries a WHOLE-CASTER snapshot taken when the server handled THAT request.
 * Toggle tile A then tile B quickly and A's push arrives describing a world where B is still
 * unflagged — re-rendering from it reverts B's paint until B's own push lands. Owner-reported
 * 2026-07-20: "if I click them one after the other the WS pushes sort of overwrite previous
 * positions". So local intent is held here and RE-ASSERTED after any re-render until it expires.
 *
 * Bounded on purpose: entries lapse after WARES_PENDING_TTL_MS so the server always wins in the end
 * (another player, a trade, or a GM edit must not be fought forever by a stuck local override).
 */
const WARES_PENDING_TTL_MS = 10000;
const waresPending = new Map<string, { desired: number; until: number; casterEcId: string; timer: number }>();

/**
 * Last value the SERVER is known to have held, per tile. Seeded from the DOM at the moment a tile
 * has no local override (so the pixels are server truth), then advanced on each accepted POST.
 * A rejected write rolls back to THIS, not to `!newVal` — with two rapid rejected clicks (1 then 0)
 * inverting the latest intent would land the UI on 1 when the server never left 0.
 */
const waresConfirmed = new Map<string, number>();

/** Re-run the search/type/hide-wares filter: visibility is inline `display` derived from
 *  data-is_wares (filter-search-init), so painting the attribute alone can leave a freshly
 *  flagged tile visible while "hide wares" is on. */
function refreshWaresFilter(): void {
    invCall('fateTideRefreshInventoryUnequippedSearch');
}

function clearWaresPending(invId: string): void {
    const existing = waresPending.get(invId);
    if (existing) clearTimeout(existing.timer);
    waresPending.delete(invId);
}

function setWaresPending(invId: string, desired: number, casterEcId: string): void {
    clearWaresPending(invId);
    // Expire on a TIMER, not only when some other mutation happens to fire the observer — otherwise
    // a quiet page keeps the override forever. On expiry, hand the tile back to the server by
    // repainting from the (authoritative) caster cache rather than just dropping the entry.
    const timer = window.setTimeout(() => {
        waresPending.delete(invId);
        invCall('ftRepaintCasterFromCache', casterEcId);
        refreshWaresFilter();
    }, WARES_PENDING_TTL_MS);
    waresPending.set(invId, { desired, until: Date.now() + WARES_PENDING_TTL_MS, casterEcId, timer });
}

function reapplyPendingWares(): void {
    let repainted = false;
    for (const [invId, pending] of waresPending) {
        const sel = `.inventory-item[data-inventory_id="${invId.replace(/["\\]/g, '\\$&')}"]`;
        const want = String(pending.desired);
        let stale = false;
        document.querySelectorAll<HTMLElement>(sel).forEach((tile) => {
            if (tile.getAttribute('data-is_wares') !== want) stale = true;
        });
        if (stale) { paintWaresTile(invId, pending.desired === 1); repainted = true; }
    }
    if (repainted) refreshWaresFilter();
}

// Re-assert after ANY re-render of the tiles, whoever caused it (websocket caster_update, cache
// repaint, tab switch). Cheap: the callback exits immediately while nothing is pending, and our own
// repaint converges (the next pass finds the DOM already matching, so it does not loop).
let waresReapplyQueued = false;
function startWaresReapplyObserver(): void {
    if (!document.body) return;
    new MutationObserver(() => {
        if (!waresPending.size || waresReapplyQueued) return;
        waresReapplyQueued = true;
        requestAnimationFrame(() => { waresReapplyQueued = false; reapplyPendingWares(); });
    }).observe(document.body, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: ['data-is_wares'],
    });
}
if (document.body) startWaresReapplyObserver();
else document.addEventListener('DOMContentLoaded', startWaresReapplyObserver, { once: true });

function toggleWares(invId: string, newVal: number, casterEcId: string): void {
    if (!invId || !casterEcId) return;
    // Optimistic cache patch (generic key/value patcher; the WS caster_update reconciles), then
    // the idempotent POST — the endpoint re-validates user control + root ownership + PC-ness.
    // We claim casterEcId (NOT the ACTIVE caster): the item's root owner is the SHEET caster, so
    // force-overriding it would break toggling on a co-owned second PC's sheet.
    // ORDER MATTERS: cache patch FIRST, DOM paint LAST. ftApplyInventoryEditToCache synchronously
    // re-renders the sheet grid (ftRepaintCasterFromCache) and that re-render does NOT reflect the
    // patched inventory_wares — so painting first got silently clobbered inside the same frame
    // (measured: still flipping only on the ~1.5s websocket). Painting after the re-render leaves
    // our markers as the last write, and the later WS re-render carries the confirmed value.
    // No override in flight ⇒ the tile currently shows SERVER truth. Record it now, while it is
    // still trustworthy, so a rejected write has a real value to fall back to.
    // INV2 S1 (2026-09-27): the write is a client-truth flag commit (janus-engine/inventory/flag-adjust.ts →
    // inventory_flag_truth_commit_inc.php): authority from the row (the root holder IS this PC and it is the user's), a CAS on the
    // row's holder + flag, and an in-place cache patch instead of the legacy recompile fan-out. Commits for one tile are serialized
    // by the shared per-row queue, so rapid clicks land in order.
    const cached = (window as unknown as { FateTide?: { casterCache?: Map<string, unknown> } }).FateTide?.casterCache?.get(casterEcId) as Record<string, unknown> | undefined;
    const node = findInventoryRow(cached, invId);
    const possessId = String((node?.inventory as Record<string, unknown> | undefined)?.possess_id ?? casterEcId);
    const encounterId = String((cached?.encounter_creatures as Record<string, unknown> | undefined)?.encounter_id
        ?? (window as unknown as { ftGetActiveEncounterId?: () => string }).ftGetActiveEncounterId?.() ?? '');
    // No override in flight ⇒ the tile currently shows SERVER truth. Record it now, while it is
    // still trustworthy, so a rejected write has a real value to fall back to.
    if (!waresPending.has(invId)) {
        const shown = document.querySelector(`.inventory-item[data-inventory_id="${invId.replace(/["\\]/g, '\\$&')}"]`)
            ?.getAttribute('data-is_wares');
        if (shown === '0' || shown === '1') waresConfirmed.set(invId, Number(shown));
    }
    const seedValue: Bit = waresConfirmed.get(invId) === 1 ? 1 : waresConfirmed.get(invId) === 0 ? 0 : (newVal === 1 ? 0 : 1);
    setWaresPending(invId, newVal, casterEcId);
    invCall('ftApplyInventoryEditToCache', invId, { inventory_wares: newVal });
    paintWaresTile(invId, newVal === 1);
    refreshWaresFilter();   // visibility is filter-driven — see refreshWaresFilter()

    // FIRE-AND-FORGET (owner 2026-07-20: "this is a minor update, we can assume it is correct"): the optimistic
    // cache patch above already repainted the tile, so the happy path never touches the DOM again.
    //
    // Only the NEWEST in-flight toggle for this tile may roll back. Without the generation guard,
    // three rapid clicks (paint 1 → 0 → 1) let a slow failure from the FIRST request revert to 0
    // and clobber the later, correct paint — and it could equally overwrite a WS reconcile.
    const seq = (waresRequestSeq.get(invId) ?? 0) + 1;
    waresRequestSeq.set(invId, seq);

    const fail = (message: string, saved?: Bit): void => {
        if (saved === 0 || saved === 1) waresConfirmed.set(invId, saved);   // the server named its truth
        if (waresRequestSeq.get(invId) !== seq) return;   // superseded — the newer click governs
        // Roll back to the last value the SERVER actually held, not to !newVal. Two rapid rejected
        // clicks (1 then 0) would otherwise land the UI on 1 though the server never left 0. With no
        // recorded value, hand the tile back to the authoritative cache instead of guessing.
        const confirmed = waresConfirmed.get(invId);
        if (confirmed === 0 || confirmed === 1) {
            setWaresPending(invId, confirmed, casterEcId);
            invCall('ftApplyInventoryEditToCache', invId, { inventory_wares: confirmed });
            paintWaresTile(invId, confirmed === 1);   // paint last — see the ordering note above
        } else {
            clearWaresPending(invId);
            invCall('ftRepaintCasterFromCache', casterEcId);
        }
        refreshWaresFilter();
        invCall('displayWarning', message);
    };

    type FlagCommit = typeof queueInventoryFlag;
    const commit = (window as unknown as { ftCommitInventoryFlag?: FlagCommit }).ftCommitInventoryFlag ?? queueInventoryFlag;
    void commit({ kind: 'wares', encounterId, inventoryId: invId, actorId: casterEcId, desired: newVal === 1 ? 1 : 0, seed: { value: seedValue, possessId } })
        .then((answer) => {
            if (answer.ok) {
                // Accepted — this value is now what the server holds, so it becomes the rollback
                // floor for any LATER rejected toggle of this tile. The per-row queue settles commits in
                // order, so every success advances it, not only the newest click's (Codex review INV2 S1 #9).
                waresConfirmed.set(invId, newVal);
                return;
            }
            fail(answer.status === 409 ? 'That item changed elsewhere — its current wares mark was restored.'
                : answer.status === 403 ? String(answer.error || 'You can only mark your own items as wares.')
                : 'Could not update wares — please try again.', answer.saved ? answer.saved.value === 1 ? 1 : 0 : undefined);
        })
        .catch(() => fail('Could not update wares — please try again.'));
}

// Click-after-drag guard: DragManager's custom drag ends with a mouseup that can still synthesize
// a click on the tile; a shift-drag (ground-drop placement) must not ALSO toggle wares. Track the
// last mousedown point and ignore wares shift-clicks whose pointer travelled past the same 6px
// threshold the drag layer uses to distinguish click from drag.
let waresPointerDownX = 0;
let waresPointerDownY = 0;
document.addEventListener('mousedown', (event) => {
    waresPointerDownX = event.clientX;
    waresPointerDownY = event.clientY;
}, true);

document.addEventListener('click', (event) => {
    // Shift+left-click on an inventory tile toggles WARES (desktop fast path). Scoped to tiles in
    // search zones under a wares-enabled root — excludes the equipment paper-doll (not in a zone)
    // and every loot/peek/NPC surface (root not stamped).
    if (event.shiftKey && event.target instanceof Element) {
        const tileEl = event.target.closest<HTMLElement>('.inventory-item');
        const waresRoot = tileEl?.closest<HTMLElement>('[data-ft-wares-enabled="1"]');
        if (
            tileEl
            && waresRoot
            && tileEl.closest('[data-inventory-search-zone="1"]')
            && Math.hypot(event.clientX - waresPointerDownX, event.clientY - waresPointerDownY) <= 6
        ) {
            event.preventDefault();
            event.stopPropagation();
            const invId = tileEl.getAttribute('data-inventory_id') || '';
            const currentlyWares = tileEl.getAttribute('data-is_wares') === '1';
            // The search root's id embeds the sheet caster: div_inventory_encounter_creature_<ecId>.
            const sheetEcId = waresRoot.id.replace('div_inventory_encounter_creature_', '');
            toggleWares(invId, currentlyWares ? 0 : 1, sheetEcId);
            return;
        }
    }

    const el = event.target instanceof Element
        ? event.target.closest<HTMLElement>('[data-ft-inv-action]')
        : null;
    if (!el) return;
    const action = el.dataset.ftInvAction || '';
    if (action === 'toggle-hide-wares') {
        event.preventDefault();
        const ecIdStr = el.dataset.ftEcId || '';
        const rootIdStr = el.dataset.ftRootId || '';
        const on = !isHideWaresOn(ecIdStr);
        setHideWares(ecIdStr, on);
        const root = document.getElementById(rootIdStr);
        if (root) {
            if (on) root.setAttribute('data-ft-inventory-hide-wares', '1');
            else root.removeAttribute('data-ft-inventory-hide-wares');
        }
        el.classList.toggle('active', on);
        el.setAttribute('aria-pressed', String(on));
        invCall('fateTideRefreshInventoryUnequippedSearch');
        return;
    }
    if (action === 'open-info') { event.preventDefault(); invCall('openItemInfo', el.dataset.ftItemId || ''); return; }
    if (action === 'pickpocket-confirm') { event.preventDefault(); invCall('pickpocketConfirm'); return; }
    if (action === 'pickpocket-clear') { event.preventDefault(); invCall('pickpocketClear'); return; }
    if (action === 'action-refresh-data') {
        event.preventDefault();
        invCall('actionRefreshData', el.dataset.ftUrl || '', {});
        return;
    }
    if (action === 'equip-hands') {
        // I2/I3 S2 → N6b (R-088): the three hand buttons are client truth only (the legacy swap_hands / equip_both_hands /
        // unequip_both_hands endpoints are deleted); what the page cannot decide is refused with words.
        event.preventDefault();
        const kind = el.dataset.ftKind;
        if (kind === 'swap_hands' || kind === 'both_hands' || kind === 'unequip_both_hands') void invCall('ftEquipHands', kind);
        return;
    }
    if (action === 'open-side-form') { event.preventDefault(); invCall('openSideForm', el.dataset.ftUrl || ''); return; }
    if (action === 'weight-breakdown') { event.preventDefault(); invCall('openInventoryWeightBreakdown', el.dataset.ftEncounterCreatureId || ''); return; }
    if (action === 'onhand-weight-breakdown') { event.preventDefault(); invCall('openInventoryOnHandWeightBreakdown', el.dataset.ftEncounterCreatureId || ''); return; }
    if (action === 'onhand-gp-breakdown') { event.preventDefault(); invCall('openInventoryOnHandGpBreakdown', el.dataset.ftEncounterCreatureId || ''); return; }
    if (action === 'container-weight-breakdown') { event.preventDefault(); invCall('openContainerWeightBreakdown', el.dataset.ftEncounterCreatureId || '', el.dataset.ftInventoryId || ''); return; }
    if (action === 'container-gp-breakdown') { event.preventDefault(); invCall('openContainerGpBreakdown', el.dataset.ftEncounterCreatureId || '', el.dataset.ftInventoryId || ''); return; }
    if (action === 'toggle-section') { event.preventDefault(); invCall('ftToggleInvSection', el.dataset.ftViewerId || '', el.dataset.ftSection || ''); return; }
    if (action === 'auto-sort') { event.preventDefault(); invCall('ftAutoSortInvWindows', el.dataset.ftViewerId || ''); }
});

document.addEventListener('keydown', (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    const el = event.target instanceof Element
        ? event.target.closest<HTMLElement>('[data-ft-inv-action]')
        : null;
    if (!el) return;
    event.preventDefault();
    el.click();
});

function num(v: unknown, fallback = 0): number {
    const n = Number(Array.isArray(v) ? v[0] : v);
    return Number.isFinite(n) ? n : fallback;
}
function ec(caster: CachedCaster): Record<string, unknown> {
    return caster.encounter_creatures ?? {};
}
function ecId(caster: CachedCaster): string {
    const v = ec(caster).encounter_creature_id;
    return String((Array.isArray(v) ? v[0] : v) ?? '');
}

/**
 * Add-inventory side-form URL, carrying the caster's campaign for the server's TABLE LICENSE (a
 * campaign lends its members' PURCHASED sources, so loot from a source the GM owns is addable here).
 * openSideForm() posts an EMPTY payload, so the URL is the only way to give that context-free form any
 * context; it re-emits the id to the search JS. The server re-checks membership on every use, so this
 * is context, not a credential — a wrong/absent id just falls back to personal entitlements.
 */
function addInventoryFormUrl(caster: CachedCaster): string {
    const base = BASE_URL + 'options/tools/inventory/forms/form_add_inventory_inc.php';
    const raw = caster.player_characters?.attached_campaign_id ?? ec(caster).campaign_id;
    const camp = String((Array.isArray(raw) ? raw[0] : raw) ?? '').trim();
    const params = new URLSearchParams();
    if (camp !== '') params.set('campaign_id', camp);
    // The RECEIVING creature. ftAdjustForCaster() otherwise falls back to the ambient active caster,
    // which is right on the sheet but wrong in the fullscreen encounter, where the master control can
    // be open for a creature that is NOT the one whose turn it is. Same shape-only trust as the
    // campaign id: the server re-derives $caster_arr and re-checks permission on the add.
    const target = ecId(caster);
    if (target !== '') params.set('caster_id', target);
    const qs = params.toString();
    return qs !== '' ? `${base}?${qs}` : base;
}

const equippedSlotId = inventoryEquippedSlot;
const isContainerEntry = isInventoryDisplayContainer;

function isPrivate(entry: unknown): boolean {
    return num(ftInventoryField(entry, 'inventory_private')) === 2;
}

function statusHas(arr: unknown, id: string): boolean {
    return ftCreatureStatuses(arr, 'target').some((e) => String(ftItemsCol(e, 'item_id') ?? '') === id);
}

/** Share the card's viewer-bound target-status gate with the inventory pane. */
function hasPeekInBags(viewerId: string, caster: CachedCaster): boolean {
    return UUID_RE.test(viewerId) && creatureDisplayAccess(caster, viewerId).peek;
}

/**
 * Pickpocket staging zone — 1:1 port of _inventory_inc.php:2-52. Same element IDs (the DragManager drop
 * handler + pickpocket-init.js read them by getElementById) and data-target_id. Confirm/Clear fire the
 * pickpocket-init.js window globals via string onclicks (no TS import needed).
 */
function buildPickpocketStagingZone(targetEcId: string): string {
    const target = escapeHtml(targetEcId);
    return `<div id="pickpocket_staging_zone" class="border border-warning rounded p-2 mb-2 text-center" style="border-style: dashed !important; min-height: 80px; background: rgba(255, 193, 7, 0.08);" data-target_id="${target}">`
        + `<div id="pickpocket_staging_empty" class="py-2"><span class="text-muted">${iconMap('pickpocket', 20, 20)} Drag inventory here to pickpocket</span></div>`
        + `<div id="pickpocket_staging_content" class="d-none">`
        + `<div class="d-flex flex-column align-items-center mb-2">`
        + `<div id="pickpocket_staged_thumbnail" class="d-inline-block"></div>`
        + `<div class="text-center mt-1"><strong id="pickpocket_staged_name"></strong></div>`
        + `<div class="small mt-1">Qty: <input type="number" id="pickpocket_staged_qty" class="form-control form-control-sm d-inline-block" style="width: 65px;" min="1" value="1" /> <span class="text-muted">/ <span id="pickpocket_staged_max_qty">1</span></span></div>`
        + `<div class="small mt-1">${iconMap('weight', 16, 16)} <span id="pickpocket_staged_weight">0</span> lbs &middot; <span id="pickpocket_staged_size"></span></div>`
        + `<div class="mt-1"><span class="badge bg-danger">Attention Modifier: +<span id="pickpocket_attention_mod">0</span></span></div>`
        + `</div>`
        + `<div class="d-flex justify-content-center gap-2">`
        + `<button id="btn_pickpocket_accept" class="btn btn-sm btn-success text-dark" ${invActionAttrs('pickpocket-confirm')}>${iconMap('pickpocket', 16, 16)} Pickpocket</button>`
        + `<button id="btn_pickpocket_clear" class="btn btn-sm btn-outline-secondary border" ${invActionAttrs('pickpocket-clear')}>${iconMap('close', 16, 16)} Clear</button>`
        + `</div>`
        + `</div></div>`;
}

/** Inventory-icon size cookie (PHP reads $_COOKIE['inventory_icon_size'] ?? 70). */
function iconSize(): number {
    const m = /(?:^|;\s*)inventory_icon_size=([^;]+)/.exec(document.cookie || '');
    const n = m ? parseInt(decodeURIComponent(m[1] ?? ''), 10) : NaN;
    return Number.isFinite(n) && n > 0 ? n : 70;
}

// make_number_pretty(): commas; 2 decimals only when there is a fractional part.
function makeNumberPretty(value: number): string {
    if (!Number.isFinite(value)) return '0';
    const hasFraction = Math.abs(value - Math.round(value)) > 0.000001;
    return value.toLocaleString('en-US', {
        minimumFractionDigits: hasFraction ? 2 : 0,
        maximumFractionDigits: hasFraction ? 2 : 0,
    });
}

const contentsOf = (entry: unknown): InventoryEntry[] => inventoryContents(entry) as InventoryEntry[];

// ── tile rendering (reuse the canonical drag-drop tile) ──
interface InventoryTileOptions {
    show_overlay: boolean;
    use_size_based_width: boolean;
    thumbnail_width: number;
    thumbnail_height: number;
    is_draggable: boolean;
    show_log_message: number;
    show_icons: boolean;
    additional_classes?: string[];
    trade_owner_ec_id?: string;
    dom_id?: string;
}

function tile(entry: unknown, opts: InventoryTileOptions): string {
    const fn = window.renderInventoryThumbnail;
    if (typeof fn !== 'function') return '';
    // flow_layout:true matches ft_inventory_tile_mount (off-map tiles flow position:relative; the
    // renderer defaults to position:absolute for the XZ map).
    return fn(entry, {}, { flow_layout: true, ...opts });
}

function equipTile(entry: unknown, draggable: boolean): string {
    return tile(entry, {
        show_overlay: false,
        use_size_based_width: false,
        thumbnail_width: 68,
        thumbnail_height: 68,
        is_draggable: draggable,
        show_log_message: 1,
        show_icons: true,
    });
}

// On-hand uses the inventory_icon_size cookie (default 70); container contents are fixed 70 (mirrors PHP).
function gridTile(entry: unknown, draggable: boolean, owner: string, size = iconSize(), domSuffix = ''): string {
    return tile(entry, {
        show_overlay: false,
        use_size_based_width: false,
        thumbnail_width: size,
        thumbnail_height: size,
        is_draggable: draggable,
        show_log_message: 1,
        show_icons: false,
        additional_classes: ['m-1'],
        trade_owner_ec_id: owner,
        ...(domSuffix ? { dom_id: `inventory_${ftInventoryField(entry, 'inventory_id') ?? ''}${domSuffix}` } : {}),
    });
}

/** Empty slot — the slot's own icon, larger and borderless (owner direction 2026-06-10:
 *  the bordered mini-button wasted the 68px cell; the dimmed glyph reads as "empty slot"). */
function slotButton(slotId: string): string {
    return `<div class="d-flex align-items-center justify-content-center" style="width:68px;height:68px;opacity:.55;">${iconMap(slotId, 46, 46)}</div>`;
}

/** Primary picture url for a layer-entry nest: own pictures bucket first, then the
 *  classification carriers' (armor/weapon_classification) — the client mirror of
 *  get_thumbnail()'s classification fallback. '' when nothing resolves. */
function entryThumbUrl(entry: unknown): string {
    const picOf = window.ftPrimaryPictureOf;
    if (typeof picOf !== 'function') return '';
    const urlOf = (node: unknown): string => {
        const pic = node ? recordOf(picOf(node)) : null;
        return String(pic?.picture_url ?? pic?.pic_url ?? '');
    };
    const own = urlOf(entry);
    if (own !== '') return own;
    for (const carrierLayer of ['armor_classification', 'weapon_classification']) {
        const layers = recordOf(recordOf(entry)?.item_layer_types);
        const slot = layers?.[carrierLayer];
        const carriers = Array.isArray(slot) ? slot : (slot ? [slot] : []);
        for (const c of carriers) {
            const u = urlOf(c);
            if (u !== '') return u;
        }
    }
    return '';
}

/** Intrinsic natural armor for the paper-doll's natural-armor slot (owner request 2026-06-10).
 *  Scales/hide live on the `natural_armor` LAYER (creature taxonomy carriers included), never as
 *  an equipped inventory row — so that slot sat permanently empty for every creature. Render the
 *  first layer entry as a click-through thumb (own picture → classification picture → registry
 *  icon → type icon), read-only (intrinsic armor can't be unequipped/dragged). Works for PCs with
 *  ancestral natural armor too. Null when the caster has none → empty slot button. */
function naturalArmorTile(caster: CachedCaster): string | null {
    for (const entry of ftLayerEntries(caster, 'natural_armor')) {
        if (entry === null || entry === undefined) continue;
        const uuid = typeof entry === 'string'
            ? entry
            : itemIdOf(entry);
        if (uuid === '') continue;
        // The entry is almost always a bare UUID string: PC/creature intrinsic natural armor lives in
        // computed.creature.abilities.natural_armor as a REF (e.g. "Natural Armor +5"), stripped of its
        // nest. A bare string carries no picture/name/layers — the natural armor's real art lives on its
        // armor_classification carrier ("Natural Armors" → its picture), reachable only from the full
        // node. Resolve the ref to its full nested catalog copy (mirrors weapon-tables.ts) so
        // entryThumbUrl can walk to the picture and the tooltip/name resolve. Falls back gracefully when
        // the cache is cold (name via ftItemNameById, art via the slot glyph).
        let node: unknown = recordOf(entry);
        if (!node && typeof window.ftFindFullNestedItem === 'function') {
            node = window.ftFindFullNestedItem(uuid) || null;
        }
        let name = node ? String(ftItemsCol(node, 'item_name') ?? '') : '';
        if (name === '' && typeof ftItemNameById === 'function') name = String(ftItemNameById(uuid) || '');
        if (name === '') name = 'Natural Armor';
        const reg = (typeof ICON_LOOKUP !== 'undefined') ? ICON_LOOKUP : undefined;
        const url = node ? entryThumbUrl(node) : '';
        let art: string;
        let hasArt = false; // true only when the art DISTINGUISHES the item from the slot glyph
        if (url !== '') {
            art = `<img src="${escapeHtml(url)}" alt="${escapeHtml(name)}" class="img-thumbnail" style="width:66px;height:66px;object-fit:cover;">`;
            hasArt = true;
        } else if (reg && reg[uuid]) {
            art = iconMap(uuid, 46, 46);
            hasArt = true;
        } else {
            // No picture, no bespoke icon (the common case: a bare-UUID intrinsic entry) — fall back to
            // the natural_armor slot glyph as the main art.
            art = iconMap('natural_armor', 46, 46);
        }
        // Slot-type corner badge ONLY when the main art is a distinguishing picture/icon. When the art
        // already IS the natural_armor slot glyph (the fallback above), the badge would just stamp the
        // SAME glyph again — the "two natural-armor icons in one cell" the slot showed for Brock
        // (owner-flagged 2026-06-14). renderInventoryThumbnail's badge serves the same role only on
        // real photo tiles, so this matches it.
        const slotBadge = hasArt
            ? `<span class="position-absolute" style="top:-5px;right:0;z-index:3;pointer-events:none;">${iconMap(SLOT.natural_armor, 15, 15)}</span>`
            : '';
        return `<div class="d-flex align-items-center justify-content-center position-relative" role="button" style="width:68px;height:68px;"
            ${invInfoAttrs(uuid)}
            data-bs-toggle="tooltip" data-bs-original-title="${escapeHtml(name)}">${art}${slotBadge}</div>`;
    }
    return null;
}

/** Merged intrinsic natural weapons: the pristine `natural_weapon` layer (Bite/Claw on a creature
 *  template) + the DERIVED computed.creature.natural_weapon grants (the Living Weapon slam), deduped by
 *  UUID. ftLayerEntries only reads the pristine bucket / legacy top-level slot — it does NOT bridge the
 *  computed grant — so a PC whose only natural weapon is the derived slam would come up empty without
 *  this. Client twin of PHP creature_natural_weapon_entries (build_item_arr.php). */
function naturalWeaponEntries(caster: CachedCaster): unknown[] {
    const base = ftLayerEntries(caster, 'natural_weapon') || [];
    const derived = caster?.computed?.creature?.natural_weapon;
    if (!Array.isArray(derived) || derived.length === 0) return base;
    const uuidOf = itemIdOf;
    const seen: Record<string, boolean> = {};
    const out: unknown[] = [];
    for (const e of base) { const u = uuidOf(e); if (u !== '') seen[u] = true; out.push(e); }
    for (const d of derived) { const u = uuidOf(d); if (u !== '' && seen[u]) continue; if (u !== '') seen[u] = true; out.push(d); }
    return out;
}

/** Intrinsic natural weapons (claws / bite / slam / tail) live on the `natural_weapon` LAYER +
 *  computed.creature.natural_weapon — never as an equipped inventory row — so the paper-doll hand
 *  cells sat permanently empty for a barehanded creature whose "weapon" IS its body (owner request
 *  2026-07-03). Twin of naturalArmorTile: render the intrinsic NW whose OWN equipment_slot names the
 *  requested hand slot as a read-only click-through thumb (own art → bespoke registry icon →
 *  natural_weapon glyph — the weapon's own icon, not a fixed slot glyph). Unlike natural armor the
 *  corner badge is the HAND the weapon occupies (main / off / both) — the "icon changes with how we
 *  equip it" the owner asked for. An equipped inventory row in that hand always wins (checked first in
 *  slotContent). Null when the caster has no natural weapon for that hand → the cell falls through to
 *  its empty slot glyph. */
type ResolvedNaturalWeapon = {
    uuid: string;
    node: unknown;
    /** the weapon's OWN equipment_slot (uppercased UUID), '' when unresolved */
    slotId: string;
    /** its natural_weapon_form (uppercased UUID), '' when it carries none */
    formId: string;
};

/** ftLayerValue can hand back the bare UUID, a [uuid] wrapper or a {items:{item_id}} shell depending on
 *  how lean the row is. Coerce to an uppercased bare UUID, '' when it isn't one. */
function layerUuid(node: unknown, layer: string): string {
    let v: unknown = node ? ftLayerValue(node, layer) : null;
    if (Array.isArray(v)) v = v[0];
    const record = recordOf(v);
    if (record) v = recordOf(record.items)?.item_id ?? record.item_id ?? '';
    if (Array.isArray(v)) v = v[0];
    const s = String(v ?? '').trim();
    return UUID_RE.test(s) ? s.toUpperCase() : '';
}

/** Resolve every intrinsic natural weapon ONCE, deduped, with its own equipment_slot + form. A bare-UUID
 *  ref OR a thin computed-grant stub ({items:{item_id}} with no item_layer_types — the derived Living
 *  Weapon slam) carries neither slot nor art, so only trust an entry that already holds item_layer_types;
 *  otherwise resolve the UUID against the warm cache (mirrors naturalArmorTile). */
function resolveNaturalWeapons(caster: CachedCaster): ResolvedNaturalWeapon[] {
    const out: ResolvedNaturalWeapon[] = [];
    const seen: Record<string, boolean> = {};
    for (const entry of naturalWeaponEntries(caster)) {
        if (entry === null || entry === undefined) continue;
        const uuid = typeof entry === 'string' ? entry : itemIdOf(entry);
        if (uuid === '' || seen[uuid.toUpperCase()]) continue;
        seen[uuid.toUpperCase()] = true;
        const entryRecord = recordOf(entry);
        let node: unknown = entryRecord?.item_layer_types ? entryRecord : null;
        if (!node && typeof window.ftFindFullNestedItem === 'function') {
            node = window.ftFindFullNestedItem(uuid) || null;
        }
        out.push({ uuid, node, slotId: layerUuid(node, 'equipment_slot'), formId: layerUuid(node, 'natural_weapon_form') });
    }
    return out;
}

/** A creature has a TAIL — and so gets the tail equipment slot on the paper doll — when it either carries
 *  the authored `has_tail` anatomy layer or wields a natural weapon that IS its tail (tail-slotted, or a
 *  tail / stinger natural_weapon_form). Owner 2026-09-02: a tail natural weapon grants the slot on its
 *  own; has_tail exists for the tailed creatures that don't attack with it. */
function hasTailAnatomy(caster: CachedCaster, naturals: ResolvedNaturalWeapon[]): boolean {
    let flag: unknown = ftLayerValue(caster, 'has_tail');
    if (Array.isArray(flag)) flag = flag[0];
    if (Number(flag ?? 0) > 0) return true;
    return naturals.some((n) => n.slotId === SLOT.tail || n.formId === NW_FORM_TAIL || n.formId === NW_FORM_STINGER);
}

/** Render ONE resolved natural weapon as a read-only click-through thumb (own art → bespoke registry icon
 *  → natural_weapon glyph — the weapon's own icon, not a fixed slot glyph). `badgeSlotId` is the slot the
 *  cell being painted represents, which is NOT always the weapon's own slot: a lone claw mirrored into the
 *  empty off hand badges as the off hand. Unlike naturalArmorTile's conditional badge this one always
 *  shows — it IS the "icon changes with how we equip it" cue the owner asked for. */
function naturalWeaponTileFor(nw: ResolvedNaturalWeapon, badgeSlotId: string): string {
    const { uuid, node } = nw;
    let name = node ? String(ftItemsCol(node, 'item_name') ?? '') : '';
    if (name === '' && typeof ftItemNameById === 'function') name = String(ftItemNameById(uuid) || '');
    if (name === '') name = 'Natural Weapon';
    const reg = (typeof ICON_LOOKUP !== 'undefined') ? ICON_LOOKUP : undefined;
    const url = node ? entryThumbUrl(node) : '';
    let art: string;
    if (url !== '') {
        art = `<img src="${escapeHtml(url)}" alt="${escapeHtml(name)}" class="img-thumbnail" style="width:66px;height:66px;object-fit:cover;">`;
    } else if (reg && reg[uuid]) {
        art = iconMap(uuid, 46, 46);
    } else {
        // No picture, no bespoke icon (the common bare-UUID case) — the generic natural_weapon glyph.
        art = iconMap('natural_weapon', 46, 46);
    }
    const slotBadge = `<span class="position-absolute" style="top:-5px;right:0;z-index:3;pointer-events:none;">${iconMap(badgeSlotId, 15, 15)}</span>`;
    return `<div class="d-flex align-items-center justify-content-center position-relative" role="button" style="width:68px;height:68px;"
            ${invInfoAttrs(uuid)}
            data-bs-toggle="tooltip" data-bs-original-title="${escapeHtml(name)}">${art}${slotBadge}</div>`;
}

/** The natural weapon whose OWN equipment_slot names `slotId` (a head-slotted Bite, a tail Slap, a
 *  both-hands maw), or null. The HANDS no longer go through here — buildEquipmentColumn assigns them as a
 *  set so a single limb can mirror into the other hand. */
function naturalWeaponTile(caster: CachedCaster, slotId: string): string | null {
    const nw = resolveNaturalWeapons(caster).find((n) => n.slotId === slotId);
    return nw ? naturalWeaponTileFor(nw, slotId) : null;
}

// ── equipment paper-doll ──
function buildEquipmentColumn(caster: CachedCaster, inv: InventoryEntry[], draggable: boolean, id: string): string {
    const appearance = caster.encounter_creatures?.appearance;
    const hoodDefault = inv.filter(entry => equippedSlotId(entry) !== '').map(entry => {
        const source = { ...entry, catalog: caster.catalog };
        const selected = selectInventoryMeshSet(source, { role: 'world', entity: source });
        return hoodLookDefault(selected?.anchorMeshId);
    }).find(value => value !== null);
    const hoodControl = hoodDefault ? hoodStateControl(id, hoodState(appearance) ?? hoodDefault,
        typeof ftHasPlayerPermission === 'function' && ftHasPlayerPermission(caster)) : '';
    // Group equipped entries by slot uuid, in inventory order, for nth-match (slot_number) lookups.
    const bySlot = new Map<string, InventoryEntry[]>();
    for (const e of inv) {
        const s = equippedSlotId(e);
        if (s === '') continue;
        if (!bySlot.has(s)) bySlot.set(s, []);
        bySlot.get(s)!.push(e);
    }
    // Bare thumb-or-button for a slot's nth match (no .equipment-slot wrapper). The natural-armor
    // slot falls back to the caster's intrinsic natural_armor LAYER (scales/hide are never an
    // equipped inventory row) before showing as empty — an equipped row there still wins.
    const slotContent = (slotId: string, slotNumber = 1): string => {
        const list = bySlot.get(slotId);
        const entry = list && list.length >= slotNumber ? list[slotNumber - 1] : null;
        if (entry) return equipTile(entry, draggable);
        if (slotId === SLOT.natural_armor) {
            const intrinsic = naturalArmorTile(caster);
            if (intrinsic) return intrinsic;
        }
        // Empty head / tail / both-hands cell → fall back to the intrinsic natural weapon whose
        // equipment_slot names this slot (a head-slotted Bite — owner request 2026-07-09; a tail Slap —
        // owner request 2026-09-02), same as natural armor fills its own slot. An equipped inventory row
        // already returned above, so this only fires for a genuinely bare slot. The two HAND cells are
        // NOT resolved here: handsRow assigns them as a set (below) so one limb can mirror into the
        // other hand.
        if (slotId === SLOT.both_hands || slotId === SLOT.head || slotId === SLOT.tail) {
            const nw = naturalWeaponTile(caster, slotId);
            if (nw) return nw;
        }
        return slotButton(slotId);
    };
    // data-ft_slot / data-ft_slot_n identify each cell's slot for the DragManager best-slot equip
    // ghost (_syncInventoryEquipSlotGhost) — purely informational, no behavior on their own.
    const cellFor = (slotId: string, slotNumber = 1): string =>
        `<div class="equipment-slot"${slotId === SLOT.head ? ' style="position:relative"' : ''} data-ft_slot="${slotId}" data-ft_slot_n="${slotNumber}">${slotContent(slotId, slotNumber)}${slotId === SLOT.head && (bySlot.has(SLOT.head) || bySlot.has(SLOT.armor)) ? helmetStateControl(id, helmetState(caster.encounter_creatures?.appearance), typeof ftHasPlayerPermission === 'function' && ftHasPlayerPermission(caster)) : ''}</div>`;
    const emptyCell = `<div class="equipment-slot"></div>`;
    const rowOf = (cells: string[]) => `<div class="d-flex justify-content-center gap-3">${cells.join('')}</div>`;
    // Resolved once for the whole paper doll: the tail-slot grant and the hand assignment below both
    // read it, and each resolution walks the catalog per limb.
    const naturals = resolveNaturalWeapons(caster);
    const hasTail = hasTailAnatomy(caster, naturals);

    // Hands logic — scan inventory directly (count_*_equipped layers proved unreliable; see movement chunk).
    let bothHands = 0, bothHandsWeapon = 0, mainHand = 0, offHand = 0;
    let versatile = false;
    for (const e of inv) {
        const s = equippedSlotId(e);
        if (s === '') continue;
        const isWeapon = ftItemsCol(e, 'item_type') === 'weapon';
        if (s === SLOT.both_hands) { bothHands++; if (isWeapon) bothHandsWeapon++; }
        else if (s === SLOT.main_hand) mainHand++;
        else if (s === SLOT.off_hand) offHand++;
        // check_equipped_weapon_versatility(): a versatile weapon equipped in a hand slot.
        if (isWeapon && ftLayerHas(e, 'versatile_dice') &&
            (s === SLOT.main_hand || s === SLOT.off_hand || s === SLOT.both_hands)) {
            versatile = true;
        }
    }

    let actions = '';
    if (bothHands === 0 && (mainHand === 1 || offHand === 1)) {
        actions += `<button class="btn btn-info btn-sm m-1" ${invActionAttrs('equip-hands', { kind: 'swap_hands' })}>${iconMap('swap_hands')}</button>`;
    }
    if (versatile && bothHandsWeapon === 1) {
        actions += `<button class="btn btn-info btn-sm m-1" ${invActionAttrs('equip-hands', { kind: 'unequip_both_hands' })}>${iconMap('unequip_both_hands')}</button>`;
    } else if (versatile && bothHandsWeapon === 0 && (mainHand === 0 || offHand === 0)) {
        actions += `<button class="btn btn-info btn-sm m-1" ${invActionAttrs('equip-hands', { kind: 'both_hands' })}>${iconMap('equip_both_hands')}</button>`;
    }

    // both_hands fills BOTH hand cells; the off-hand (right) copy is a heavily faded "ghost" so it
    // reads as the same weapon occupying both hands, not a second weapon. Kept in sync with the
    // creature grid (functions.php) + equipment-grid.ts.
    // A both-hands NATURAL weapon (with NO inventory occupying either hand) is treated the same — it
    // fills both cells.
    const nwBothHands = (bothHands === 0 && mainHand === 0 && offHand === 0)
        ? naturalWeaponTile(caster, SLOT.both_hands) : null;
    const showBoth = bothHands >= 1 || nwBothHands !== null;
    const bothContent = bothHands >= 1 ? slotContent(SLOT.both_hands, 1) : (nwBothHands ?? '');

    // ── single-hand NATURAL weapons: assigned as a SET, not per-cell (owner 2026-09-02) ──────────
    // Two rules the old per-cell fallback could not express:
    //  1. A creature with ONE hand natural weapon (a claw) and both hands bare shows it in the main
    //     hand and GHOSTS it into the off hand — the same treatment a two-handed weapon gets. It is
    //     one limb drawn twice, so it is NOT a second weapon (see calculate_equipped_counts.php).
    //  2. Equipping a main-hand weapon does NOT take the claw away: the unclaimed natural weapon
    //     slides into the still-free off hand as a REAL occupant, which is what feeds two-weapon
    //     fighting (dagger main + claw off).
    // Inventory always wins in the exact hand it occupies.
    const mainFree = bothHands === 0 && mainHand === 0;
    const offFree = bothHands === 0 && offHand === 0;
    let nwMain: ResolvedNaturalWeapon | null = null;
    let nwOff: ResolvedNaturalWeapon | null = null;
    let nwOffIsMirror = false;
    if (!showBoth && (mainFree || offFree)) {
        const pool = naturals.filter((n) => n.slotId === SLOT.main_hand || n.slotId === SLOT.off_hand);
        const take = (want: string): ResolvedNaturalWeapon | null => {
            const i = pool.findIndex((n) => n.slotId === want);
            return i >= 0 ? (pool.splice(i, 1)[0] ?? null) : null;
        };
        if (mainFree) nwMain = take(SLOT.main_hand);
        if (offFree) nwOff = take(SLOT.off_hand);
        // A free hand with no natural weapon of its own borrows an unclaimed one from the other hand —
        // "in most cases we don't have two that fit into the main or off hand slots" (owner).
        if (mainFree && !nwMain && pool.length > 0) nwMain = pool.shift() ?? null;
        if (offFree && !nwOff && pool.length > 0) nwOff = pool.shift() ?? null;
        // Exactly one limb and BOTH hands bare → mirror it, main real / off ghosted — UNLESS it is a
        // PAIRED limb (fist/slam, claw): two fists are two weapons, both cells real (owner 2026-09-03;
        // shared/paired-limb-forms.ts, twin of calculate_equipped_counts.php).
        if (mainFree && offFree && (nwMain === null) !== (nwOff === null)) {
            const solo = nwMain ?? nwOff!;
            nwMain = solo;
            nwOff = solo;
            nwOffIsMirror = !(isPairedLimb(solo.formId, solo.uuid) && bodyPairsLimbs(ftLayerValue(caster, 'quadruped'), ftItemsCol(caster, 'item_type')));
        }
    }
    const mainContent = showBoth
        ? bothContent
        : (nwMain ? naturalWeaponTileFor(nwMain, SLOT.main_hand) : slotContent(SLOT.main_hand, 1));
    const offNatural = nwOff ? naturalWeaponTileFor(nwOff, SLOT.off_hand) : '';
    const offContent = showBoth
        ? `<div style="opacity: 0.3;">${bothContent}</div>`
        : (nwOff
            ? (nwOffIsMirror ? `<div style="opacity: 0.3;">${offNatural}</div>` : offNatural)
            : slotContent(SLOT.off_hand, 1));
    const handsRow = `<div class="d-flex justify-content-center gap-3 align-items-center">`
        + `<div class="equipment-slot" data-ft_slot="${SLOT.main_hand}" data-ft_slot_n="1">${mainContent}</div>`
        + `<div class="equipment-slot d-flex flex-column gap-1">${actions}</div>`
        + `<div class="equipment-slot" data-ft_slot="${SLOT.off_hand}" data-ft_slot_n="1">${offContent}</div>`
        + `</div>`;

    const dropAttrs = draggable
        ? ` data-move_to_inventory_id="${escapeHtml(id)}" data-dropzone_type="dropzone_equip_inventory" data-dropzone_id="${escapeHtml(id)}"`
        : '';

    return `<div class="d-flex align-items-center justify-content-center mt-1 mb-2 mx-2 border rounded${draggable ? ' dropzone' : ''}"${dropAttrs}>`
        + `<div class="d-flex flex-column gap-3">`
        + rowOf([cellFor(SLOT.air_around), cellFor(SLOT.head), cellFor(SLOT.neck)])
        + rowOf([cellFor(SLOT.natural_armor), cellFor(SLOT.armor), cellFor(SLOT.back)])
        + rowOf([cellFor(SLOT.bracers), cellFor(SLOT.clothing), cellFor(SLOT.cloak)])
        + rowOf([cellFor(SLOT.ring, 1), cellFor(SLOT.breast), cellFor(SLOT.ring, 2)])
        + rowOf([cellFor(SLOT.waist, 1), cellFor(SLOT.waist, 2), cellFor(SLOT.waist, 3)])
        // The bottom-left cell is the TAIL slot for tailed creatures, and stays blank for everyone else —
        // a paper doll should not offer a limb the body doesn't have (owner 2026-09-02).
        + rowOf([hasTail ? cellFor(SLOT.tail) : emptyCell, cellFor(SLOT.legs), cellFor(SLOT.feet)])
        + handsRow
        + `</div>${hoodControl}</div>`;
}

// ── on-hand grid ──
function buildOnHandColumn(caster: CachedCaster, inv: InventoryEntry[], draggable: boolean, rootId: string,
    { withContainers = true, hideTitle = false, tradeStock = false, themedEmpty = false } = {}): string {
    const id = ecId(caster);
    const domSuffix = tradeStock ? '_trade_merchant' : '';
    const totals = inventoryOnHandTotals(inv, id);
    const onHand = totals.rows as InventoryEntry[];
    const lb = makeNumberPretty(Math.round(totals.weight * 100) / 100);
    const gp = makeNumberPretty(totals.gp);
    const ecAttr = escapeHtml(id);

    // Banner = FLEX, not absolute-positioned badges: the old absolute strip overlapped the
    // centered title once the column narrowed beside the equipment grid (owner-flagged
    // 2026-06-10 — "On H[badges]d"). Title left, stats right, wrapping to a second banner
    // line when the column is too tight — the info stays visible at every width.
    const header = `<div class="ft-onhand-banner d-flex flex-wrap align-items-center justify-content-between column-gap-2 row-gap-1 mb-0 pt-2 pb-2 px-2 bg-secondary text-dark bg-gradient rounded-top">`
        + (hideTitle ? '' : `<h5 class="mb-0 text-nowrap">On Hand</h5>`)
        + `<div class="d-flex align-items-center gap-2 ms-auto">`
        + `<span role="button" tabindex="0" class="ft-container-stats-strip ft-container-stats-strip--clickable user-select-none py-1 px-2 rounded border d-inline-flex align-items-center gap-1" style="cursor: pointer; white-space: nowrap;" aria-label="On-hand weight breakdown" ${invActionAttrs('onhand-weight-breakdown', { encounterCreatureId: ecAttr })}>${iconMap('weight', 18, 18)}<span class="badge bg-light text-dark border border-dark">${escapeHtml(lb)}</span></span>`
        + `<span role="button" tabindex="0" class="ft-container-stats-strip ft-container-stats-strip--clickable user-select-none py-1 px-2 rounded border d-inline-flex align-items-center gap-1" style="cursor: pointer; white-space: nowrap;" aria-label="On-hand GP value breakdown" ${invActionAttrs('onhand-gp-breakdown', { encounterCreatureId: ecAttr })}>${iconMap('cost_gp', 18, 18)}<span class="badge bg-warning text-dark border border-dark">${escapeHtml(gp)}</span></span>`
        + `</div></div>`;

    const dropAttrs = draggable
        ? ` data-dropzone_type="dropzone_move_inventory" data-dropzone_id="${ecAttr}" data-merchant="${tradeStock ? '1' : '0'}"`
        : '';
    const tiles = onHand.length
        ? onHand.map((e) => gridTile(e, draggable, id, iconSize(), domSuffix)).join('')
        : themedEmpty ? buildEmptyState({ art: 'chest', title: 'Nothing on hand', compact: true }).outerHTML
        : `<div class="w-100 d-flex align-items-center justify-content-center py-3" style="min-height: 50px;"><h6 class="mb-0 text-nowrap">Empty</h6></div>`;
    const grid = `<div id="inventory_${ecAttr}${domSuffix}" class="d-flex justify-content-center flex-wrap${tradeStock ? '' : ' border rounded-bottom'}${draggable ? ' dropzone' : ''}"${dropAttrs} data-inventory-search-zone="1">${tiles}</div>`;
    const content = header + grid + (withContainers ? buildContainers(caster, inv, draggable, id, domSuffix) : '');
    // The trade mount already lives inside its search root alongside the search/type controls.
    if (tradeStock) return content;

    // flex:1 1 240px — fills whatever width remains beside the natural-width equipment grid,
    // wrapping under it only when the host is genuinely too narrow (see renderInventory layout).
    // mt-1 matches the equipment grid box's top margin, so both columns' top edges align.
    // Wares: enable the shift-click affordance only on the owner's PC sheet, and re-stamp the
    // persisted hide-wares view state so it survives websocket repaints.
    const waresAttrs = (waresToggleEnabled(caster) ? ' data-ft-wares-enabled="1"' : '')
        + (isHideWaresOn(id) ? ' data-ft-inventory-hide-wares="1"' : '');
    return `<div id="${escapeHtml(rootId)}" class="fate-tide-inventory-unequipped-search-root mt-1"${waresAttrs} style="flex:1 1 240px; min-width:0;">`
        + content + `</div>`;
}

// ── equipped containers ──
/** All equipped, non-private containers on this caster (the bag set). Shared by the inline Containers
 *  section (PC-sheet tab) and the fullscreen bag-bar / bag windows. */
function equippedContainers(inv: InventoryEntry[]): InventoryEntry[] {
    return inv.filter((e) => !isPrivate(e) && equippedSlotId(e) !== '' && isContainerEntry(e));
}

function buildContainers(caster: CachedCaster, inv: InventoryEntry[], draggable: boolean, sheetEc: string, domSuffix = ''): string {
    const containers = equippedContainers(inv);
    if (!containers.length) return '';

    let out = `<div class="d-flex align-items-center justify-content-center mb-0 mt-2 pt-2 pb-2 px-2 bg-warning text-dark bg-gradient rounded-top"><h5 class="mb-0">Containers</h5></div>`;
    for (const c of containers) out += buildOneContainer(caster, c, draggable, sheetEc, false, domSuffix);
    return out;
}

/** One equipped container's card (header tile + name + weight/GP pills + contents grid). Extracted from
 *  buildContainers so a single bag can render standalone in a fullscreen bag window. With `hideName` the
 *  name line is dropped (the bag window's titlebar already shows it) — keeps the icon + stats pills. */
function buildOneContainer(caster: CachedCaster, c: InventoryEntry, draggable: boolean, sheetEc: string, hideName = false, domSuffix = ''): string {
        const invId = String(ftInventoryField(c, 'inventory_id') ?? '');
        const invIdAttr = escapeHtml(invId);
        const ecAttr = escapeHtml(sheetEc);
        // inventory_name is stored HTML-encoded in the blob (e.g. "Heward&#039;s …"); PHP echoes it
        // raw. Decode to plain text first, then re-escape — otherwise escapeHtml double-encodes the
        // entity (&#039; → &amp;#039;) and the literal "&#039;" shows on screen. Mirrors thumbnail.ts.
        const name = escapeHtml(decodeHtmlEntitiesForPlainText(String(ftInventoryField(c, 'inventory_name') ?? '')));
        const totals = inventoryRowTotals(c);
        const grossF = escapeHtml(makeNumberPretty(totals.grossLb));
        const heldF = escapeHtml(makeNumberPretty(totals.heldLb));
        const capPounds = totals.capacityLb === null ? '' : escapeHtml(makeNumberPretty(totals.capacityLb));
        const contentsGp = escapeHtml(makeNumberPretty(totals.contentsGp));

        // Keyrings hold ONLY keys (anti-metagame rule). Tag the dropzone so DragManager can
        // reject a non-key drop instantly, client-side — the server's
        // validate_move_into_keyring_keys_only() remains the authoritative gate.
        const acceptAttr = ftItemsCol(c, 'item_type') === 'keyring' ? ` data-dropzone_accept="key"` : '';
        const dropAttrs = draggable ? ` data-dropzone_type="dropzone_move_inventory" data-dropzone_id="${invIdAttr}"${acceptAttr}` : '';

        const headerTile = tile(c, {
            show_overlay: false,
            use_size_based_width: false,
            thumbnail_width: 70,
            thumbnail_height: 70,
            is_draggable: draggable,
            show_log_message: 1,
            show_icons: false,
            trade_owner_ec_id: sheetEc,
            ...(domSuffix ? { dom_id: `inventory_${invId}${domSuffix}` } : {}),
        });

        const locked = statusHas(c, ST.locked) && !ftHasCampaignPermission(c);
        let contentsHtml: string;
        if (locked) {
            contentsHtml = `<div style="min-height: 50px;" class="d-flex align-items-center justify-content-center mt-3 mb-1"><h5>${iconMap('locked')} Container is Locked</h5></div>`;
        } else {
            const items = contentsOf(c).filter((x) => x && !isPrivate(x));
            contentsHtml = items.length
                ? items.map((x) => gridTile(x, draggable, sheetEc, 70, domSuffix)).join('')
                : `<div style="min-height: 50px;" class="d-flex align-items-center justify-content-center"><div><h5>No inventory in container.</h5></div></div>`;
        }

        return `<div id="${invIdAttr}${domSuffix}" class="mt-0 mb-3 border rounded${draggable ? ' dropzone' : ''}"${dropAttrs}>`
            + `<div class="col-12 text-left p-1 d-flex justify-content-start border-bottom">`
            + headerTile
            + `<div class="pl-2 ft-container-header-body">`
            + (hideName ? '' : `<div class="ft-container-header-name mb-2"><span class="fw-bold" role="button" ${invInfoAttrs(invIdAttr)}>${name}</span></div>`)
            + `<div class="ft-container-header-pills">`
            + `<span role="button" tabindex="0" class="ft-container-stats-strip ft-container-stats-strip--clickable user-select-none py-1 px-2 rounded border" style="cursor: pointer; white-space: nowrap;" aria-label="Container weight breakdown" ${invActionAttrs('container-weight-breakdown', { encounterCreatureId: ecAttr, inventoryId: invIdAttr })}>`
            + `<span class="d-inline-flex align-items-center gap-1">${iconMap('sum_weight', 18, 18)}<span class="badge bg-light text-dark border border-dark">${grossF}</span></span>`
            + `<span class="ft-container-stats-strip__sep">&middot;</span>`
            + `<span class="d-inline-flex align-items-center gap-1">${iconMap('weight', 18, 18)}<span class="badge bg-light text-dark border border-dark">${heldF}</span><span class="text-muted">/</span>${iconMap('carrying_capacity_pounds', 18, 18)}<span class="badge bg-light text-dark border border-dark">${capPounds}</span></span>`
            + `</span>`
            + `<span role="button" tabindex="0" class="ft-container-stats-strip ft-container-stats-strip--clickable user-select-none py-1 px-2 rounded border d-inline-flex align-items-center gap-1" style="cursor: pointer; white-space: nowrap;" aria-label="GP value breakdown (contents)" ${invActionAttrs('container-gp-breakdown', { encounterCreatureId: ecAttr, inventoryId: invIdAttr })}>${iconMap('cost_gp', 18, 18)}<span class="badge bg-warning text-dark border border-dark">${contentsGp}</span></span>`
            + `</div></div></div>`
            + `<div id="inventory_container_contents_${invIdAttr}${domSuffix}" class="d-flex justify-content-center flex-wrap" data-inventory-search-zone="1">${contentsHtml}</div>`
            + `</div>`;
}

// ── header (weight strip + encumbered badges + search + Add) ──
function buildHeader(caster: CachedCaster, rootId: string, canAdd: boolean): string {
    const id = ecId(caster);
    const idAttr = escapeHtml(id);
    const rootAttr = escapeHtml(rootId);

    // Carry-weight strip — total_weight_held lives in computed.creature.weight. The max carrying
    // capacity is the `creature_max_carrying_capacity` layer SERVER-SIDE (build_item_arr → ['capacity',
    // 'max']), but the client blob never exposes it as a flat layer — the value rides in the computed
    // cluster at computed.creature.capacity.max.total. Read that (layer fallback for legacy blobs);
    // ftLayerValue alone returns null here, which silently suppressed the whole strip.
    const totalHeld = caster?.computed?.creature?.weight?.total_weight_held;
    const capacity = caster?.computed?.creature?.capacity?.max?.total
        ?? ftLayerValue(caster, 'creature_max_carrying_capacity');
    let weightStrip = '';
    if (totalHeld !== undefined && totalHeld !== null && capacity !== undefined && capacity !== null) {
        const held = Math.round(num(totalHeld) * 100) / 100;
        // Held badge intensifies with load (green→amber→red→dark) so it reads at a glance — the old flat
        // bg-info + white text was hard to read. Encumbered/heavy thresholds ride the same computed cluster.
        const encCap = num(caster?.computed?.creature?.capacity?.encumbered?.total
            ?? ftLayerValue(caster, 'creature_encumbered_carrying_capacity'));
        const heavyCap = num(caster?.computed?.creature?.capacity?.heavily_encumbered?.total
            ?? ftLayerValue(caster, 'creature_heavily_encumbered_carrying_capacity'));
        const heldBadge = carryWeightBadgeClasses(held, encCap, heavyCap, num(capacity));
        weightStrip = `<span role="button" tabindex="0" class="border border-success rounded p-2 mr-2 user-select-none d-inline-block ft-container-stats-strip--clickable" style="cursor: pointer; white-space: nowrap;" ${invActionAttrs('weight-breakdown', { encounterCreatureId: idAttr })}>${iconMap('weight', 25, 25, 'Carry weight breakdown')}: <span class="badge ${heldBadge}">${escapeHtml(String(held))}</span> / <span class="badge bg-dark text-white bg-gradient border border-light">${escapeHtml(String(capacity))}</span></span>`;
    }

    // Encumbered / heavily / over-capacity badges from the caster's own (caster-side) statuses.
    let badges = '';
    for (const eff of ftCreatureStatuses(caster, 'caster')) {
        const sid = String(ftItemsCol(eff, 'item_id') ?? '');
        if (sid === BADGE_ENCUMBERED) {
            badges += `<span role="button" ${invInfoAttrs(BADGE_ENCUMBERED)}>${iconMap(BADGE_ENCUMBERED)}<span class="badge bg-warning border">Encumbered</span></span>`;
        } else if (sid === BADGE_HEAVY) {
            badges += `<span role="button" ${invInfoAttrs(BADGE_HEAVY)}>${iconMap(BADGE_HEAVY)}<span class="badge bg-danger border">Heavily Encumbered</span></span>`;
        } else if (sid === BADGE_OVER) {
            badges += `<span role="button" ${invInfoAttrs(BADGE_OVER)}>${iconMap(BADGE_OVER)}<span class="badge bg-dark border">Over Carrying Capacity (Restrained)</span></span>`;
        }
    }

    const addBtnDesktop = canAdd
        ? `<button type="button" id="btn_add_inventory" class="btn btn-sm btn-primary ft-inv-header-addbtn ft-inv-header-addbtn--labeled d-none d-xl-inline-flex align-items-center justify-content-center" ${invActionAttrs('open-side-form', { url: addInventoryFormUrl(caster) })}>${iconMap('add_inventory')}<span>Add Inventory</span></button>`
        : '';

    const searchInput = (inputId: string) =>
        `<div class="position-relative ft-inv-search-wrap ft-inv-header-search w-100"><input type="search" class="form-control form-control-sm fate-tide-inventory-unequipped-search w-100 pe-4" id="${escapeHtml(inputId)}" data-ft-inventory-unequipped-root="${rootAttr}" placeholder="Search inventory" autocomplete="off" /><svg class="svg-icon position-absolute top-50 end-0 translate-middle-y me-2 ft-inv-search-icon" aria-hidden="true"><use href="#search"></use></svg></div>`;

    return `<div class="ft-inv-header-row my-3">`
        + `<div class="ft-inv-header-weight text-left">${weightStrip}${badges}</div>`
        + `<div class="ft-inv-header-search-add ft-inv-header-search-add--main-xl">`
        + `<label class="visually-hidden" for="input_inventory_unequipped_search_${idAttr}">Search inventory</label>`
        + searchInput('input_inventory_unequipped_search_' + id)
        + hideWaresButton(caster, rootId, 'd-none d-xl-inline-flex')
        + addBtnDesktop
        + `</div></div>`;
}

// Between-column mobile search row (xl-hidden), mirrors _inventory_inc.php.
function buildMobileSearchRow(caster: CachedCaster, rootId: string, canAdd: boolean): string {
    const id = ecId(caster);
    const idAttr = escapeHtml(id);
    const rootAttr = escapeHtml(rootId);
    const addBtn = canAdd
        ? `<button type="button" id="btn_add_inventory_mobile" class="btn btn-sm btn-primary p-1 ft-inv-header-addbtn ft-inv-header-addbtn--icon flex-shrink-0" ${invActionAttrs('open-side-form', { url: addInventoryFormUrl(caster) })} aria-label="Add inventory">${iconMap('add_inventory')}</button>`
        : '';
    return `<div class="col-12 ft-inv-mobile-search-row d-xl-none"><div class="ft-inv-mobile-search-row-inner">`
        + `<label class="visually-hidden" for="input_inventory_unequipped_search_mobile_${idAttr}">Search inventory</label>`
        + `<div class="position-relative ft-inv-search-wrap"><input type="search" class="form-control form-control-sm fate-tide-inventory-unequipped-search w-100 pe-4" id="input_inventory_unequipped_search_mobile_${idAttr}" data-ft-inventory-unequipped-root="${rootAttr}" placeholder="Search inventory" autocomplete="off" /><svg class="svg-icon position-absolute top-50 end-0 translate-middle-y me-2 ft-inv-search-icon" aria-hidden="true"><use href="#search"></use></svg></div>`
        + hideWaresButton(caster, rootId, 'flex-shrink-0')
        + addBtn
        + `</div></div>`;
}

/** Header "hide wares" view toggle — a merchant-coin button that dims flagged tiles out of the
 *  grids (equipped tiles + container headers stay visible; the filter loop only walks zone tiles).
 *  Rendered only on the owner's PC sheet; state persists per PC (module map + localStorage). */
function hideWaresButton(caster: CachedCaster, rootId: string, extraCls: string): string {
    if (!waresToggleEnabled(caster)) return '';
    const id = ecId(caster);
    const on = isHideWaresOn(id);
    return `<button type="button" class="btn btn-sm ${on ? 'btn-warning active' : 'btn-outline-secondary'} ft-inv-hide-wares-btn ${escapeHtml(extraCls)} align-items-center justify-content-center" aria-pressed="${on ? 'true' : 'false'}" title="${on ? 'Show wares' : 'Hide wares'}" ${invActionAttrs('toggle-hide-wares', { ecId: id, rootId })}>${iconMap('merchant', 18, 18)}</button>`;
}

/**
 * Render the Inventory tab from the caster blob. Returns the full pane HTML string. The router must,
 * after injecting this, call refreshDragAndDrop() + fateTideRefreshInventoryUnequippedSearch() so the
 * delegation-based drag-drop + search re-scan the freshly-painted tiles.
 */
export function renderInventory(caster: CachedCaster, _casterId: string, viewerId = '', themedEmpty = false): string {
    const refusal = (caster as Record<string, unknown>)._inventory_refusal;
    if (typeof refusal === 'string') return `<div class="alert alert-warning" role="alert" data-ft-inventory-refused="1">${escapeHtml(refusal)}</div>`;
    const id = ecId(caster);
    if (!id) return '';
    const rootId = 'div_inventory_encounter_creature_' + id;

    const inv = inventoryRows(caster.inventory).slice();
    // Sort by inventory_name ASC (matches _inventory_inc.php usort).
    inv.sort((a, b) => String(ftInventoryField(a, 'inventory_name') ?? '').localeCompare(String(ftInventoryField(b, 'inventory_name') ?? '')));

    const canAdd = ftHasPlayerPermission(caster);
    const dead = num(ec(caster).encounter_creature_dead) === 1;
    // peek (looter holds a peek-in-bags status on a LIVE target) adds the pickpocket staging zone; owner / gm /
    // dead / unconscious / peek all enable drag — the exact $ft_inv_can_drag OR (_inventory_inc.php:61-67).
    const peek = hasPeekInBags(viewerId, caster);
    const canDrag = ftHasPlayerPermission(caster) || ftHasCampaignPermission(caster) || dead || statusHas(caster, ST.unconscious) || peek;

    const equipDropAttrs = canDrag
        ? ` data-dropzone_type="dropzone_equip_inventory" data-dropzone_id="${escapeHtml(id)}"`
        : '';

    // CONTAINER-ADAPTIVE LAYOUT (2026-06-10, round 2): this pane renders in the full-page PC
    // sheet AND inside half-page creature cards (item.php creature card, GM-viewer / side-info
    // TS card). Bootstrap's col-lg-6 50/50 split only reacts to the VIEWPORT, so inside a card
    // each column ended up quarter-page and spilled. Instead: the equipment paper-doll keeps its
    // NATURAL fixed width (flex-shrink-0) and the On-Hand column flexes into whatever remains
    // (flex:1 1 280px), wrapping below the grid only when the host is genuinely too narrow.
    // Side-by-side everywhere there's room — including the side-info drawer — with no PHP view
    // flags ($opened_in_side_info), no container query, no per-host CSS.
    // The sub-xl search row sits ABOVE the grid/On-Hand pair, not between them: it's full-width,
    // so in-between it forced On-Hand onto its own row at any viewport < 1200 even when the pair
    // fit side-by-side (owner-flagged 2026-06-10). The style.css @media rules still swap it for
    // the header search at xl+.
    //
    // Side-by-side is FORCED via container query (nowrap once the pane itself is ≥ 500px wide):
    // wrap-arithmetic proved fragile across hosts — the side-info pane lands within a few px of
    // grid(~270) + On-Hand basis(240), so flex-wrap flip-flopped on it (owner-reproduced
    // 2026-06-10). Above the threshold the On-Hand column squeezes (min-width:0, its tiles
    // re-flow) instead of dropping below the grid; only genuinely narrow hosts (phone-width
    // drawers) stack.
    const layoutStyle = `<style>
.ft-pc-inventory { container-type: inline-size; }
.ft-pc-inventory .ft-inv-columns { display: flex; flex-wrap: wrap; align-items: flex-start; }
@container (min-width: 500px) {
\t.ft-pc-inventory .ft-inv-columns { flex-wrap: nowrap; }
}
/* Narrow pane (creature card / side-info): compact the On-Hand banner so the title +
   weight/gold chips fit ONE line instead of wrapping into a tall two-row banner. */
@container (max-width: 699px) {
\t.ft-pc-inventory .ft-onhand-banner { padding-top: .375rem; padding-bottom: .375rem; }
\t.ft-pc-inventory .ft-onhand-banner h5 { font-size: 1rem; }
\t.ft-pc-inventory .ft-onhand-banner .ft-container-stats-strip { padding: .125rem .375rem !important; }
\t.ft-pc-inventory .ft-onhand-banner .ft-container-stats-strip .badge { padding: .25em .4em; }
}
</style>`;

    return layoutStyle
        + `<div class="ft-pc-inventory">`
        + (peek ? buildPickpocketStagingZone(id) : '')
        + buildHeader(caster, rootId, canAdd)
        + buildMobileSearchRow(caster, rootId, canAdd)
        + `<div class="ft-inv-columns px-2">`
        + `<div id="div_inventory_equipment_slots" class="flex-shrink-0"${equipDropAttrs}>${buildEquipmentColumn(caster, inv, canDrag, id)}</div>`
        + buildOnHandColumn(caster, inv, canDrag, rootId, { themedEmpty })
        + `</div>`
        + `</div>`;
}

// ── fullscreen MASTER inventory control + section windows (WoW/BG3 model) ───────────────────────────
// These back the fullscreen-encounter inventory (NOT the in-sheet Inventory tab). "I" opens a small MASTER
// control: a strip of toggle tiles — Equipment, On Hand, and one per equipped Bag. Each tile toggles its OWN
// floating window and glows when open; the window manager (encounter-map bundle) persists which sections are
// open (sessionStorage) and restores them on re-open. Every section window reuses the SAME drag-wired tiles +
// dropzones, so equip / move / drag-to-map keep working — the manager just re-runs refreshDragAndDrop().

/** The owner/gm/dead/unconscious/peek drag gate (the $ft_inv_can_drag OR), shared by the section windows. */
function invCanDrag(caster: CachedCaster, viewerId: string): boolean {
    const dead = num(ec(caster).encounter_creature_dead) === 1;
    const peek = hasPeekInBags(viewerId, caster);
    return ftHasPlayerPermission(caster) || ftHasCampaignPermission(caster) || dead || statusHas(caster, ST.unconscious) || peek;
}

/** Inventory rows, name-sorted (matches the tab's usort), as a fresh array. */
function sortedInventory(caster: CachedCaster): InventoryEntry[] {
    const inv = inventoryRows(caster.inventory).slice();
    inv.sort((a, b) => String(ftInventoryField(a, 'inventory_name') ?? '').localeCompare(String(ftInventoryField(b, 'inventory_name') ?? '')));
    return inv;
}

/** Trade uses the same native tiles, totals and equipped bags as the sheet. The exchange bridge
 * hydrates participants through C5 before this call; a cold mount uses that same page loader. */
export async function ftPaintTradeStock(casterId: string, mount: HTMLElement): Promise<void> {
    const caster = await fetchDisplayCaster(casterId);
    const refusal = (caster as Record<string, unknown>)._inventory_refusal;
    if (typeof refusal === 'string') throw new Error(refusal);
    if (typeof window.renderInventoryThumbnail !== 'function') throw new Error('unported:merchant-stock:thumbnail-renderer-not-held');
    mount.innerHTML = buildOnHandColumn(caster, sortedInventory(caster), true, '', { tradeStock: true });
}

/** A master-control toggle tile. `key` is the section id ('equipment' | 'onhand' | 'bag:<uuid>'). The window
 *  manager exposes ftToggleInvSection / ftIsInvSectionOpen so the highlight survives caster_update repaints. */
function masterTile(viewerId: string, key: string, label: string, art: string): string {
    const vid = escapeHtml(viewerId);
    const k = escapeHtml(key);
    const isOpen = window.ftIsInvSectionOpen;
    const openCls = (typeof isOpen === 'function' && isOpen(viewerId, key)) ? ' is-open' : '';
    return `<button type="button" class="ft-mastertile d-inline-flex flex-column align-items-center${openCls}" title="${label}" data-ft-section="${k}" ${invActionAttrs('toggle-section', { viewerId: vid, section: k })}>`
        + `${art}<span class="ft-mastertile-label">${label}</span></button>`;
}

/** Fullscreen MASTER control: the strip of section toggles. Mounted via the `master_control` pane so it
 *  repaints on caster_update — a newly-equipped bag's tile appears, an emptied one's art updates. */
export function renderMasterControl(caster: CachedCaster, _casterId: string, viewerId = ''): string {
    const id = ecId(caster);
    if (!id) return '';
    const inv = sortedInventory(caster);
    const charName = escapeHtml(decodeHtmlEntitiesForPlainText(String(ftItemsCol(caster, 'item_name') ?? ''))) || 'Inventory';
    const vid = escapeHtml(viewerId);

    // Fixed sections every creature has, then an <hr>, then one tile per equipped Bag.
    const fixed = masterTile(viewerId, 'equipment', 'Equipment', iconMap('armor', 28, 28))
        + masterTile(viewerId, 'onhand', 'On Hand', iconMap(SLOT.main_hand, 28, 28));

    let bagTiles = '';
    for (const c of equippedContainers(inv)) {
        const rawCid = String(ftInventoryField(c, 'inventory_id') ?? '');
        const name = escapeHtml(decodeHtmlEntitiesForPlainText(String(ftInventoryField(c, 'inventory_name') ?? 'Bag')));
        const url = entryThumbUrl(c);
        const art = url ? `<img src="${escapeHtml(url)}" alt="${name}" class="ft-mastertile-art">` : iconMap('container', 28, 28);
        bagTiles += masterTile(viewerId, 'bag:' + rawCid, name, art);
    }

    // Add Inventory: the SAME side-form the sheet's Inventory tab opens (openSideForm -> the FT3D
    // Forms window on this route). Gated like the drag/equip half of invCanDrag: your own PC, or a
    // creature you GM. Add Inventory left, Auto-sort right.
    const canAdd = ftHasPlayerPermission(caster) || ftHasCampaignPermission(caster);
    const addBtn = canAdd
        ? `<button type="button" class="ft-master-add" ${invActionAttrs('open-side-form', { url: addInventoryFormUrl(caster) })}>`
            + `${iconMap('add_inventory', 15, 15, 'Add inventory to this creature')}<span>Add</span></button>`
        : '';

    // Auto-sort: tidy all open section windows into a grid (window manager global).
    const controls = `<div class="ft-master-controls">`
        + addBtn
        + `<button type="button" class="ft-master-autosort" ${invActionAttrs('auto-sort', { viewerId: vid })}>`
        + `${iconMap('center_axis', 15, 15, 'Auto-arrange the open windows')}<span>Auto-sort</span></button>`
        + `</div>`;

    return `<div class="ft-pc-inventory" data-ft-charname="${charName}">`
        + `<div class="ft-master">`
        + controls
        + `<div class="ft-master-group">${fixed}</div>`
        + (bagTiles ? `<hr class="ft-master-sep"><div class="ft-master-group">${bagTiles}</div>` : '')
        + `</div>`
        + `</div>`;
}

/** Equipment paper-doll window body. */
export function renderEquipmentWindow(caster: CachedCaster, _casterId: string, viewerId = ''): string {
    const id = ecId(caster);
    if (!id) return '';
    const inv = sortedInventory(caster);
    const canDrag = invCanDrag(caster, viewerId);
    const equipDropAttrs = canDrag
        ? ` data-dropzone_type="dropzone_equip_inventory" data-dropzone_id="${escapeHtml(id)}"`
        : '';
    return `<div class="ft-pc-inventory ft-secwin ft-secwin-equip px-2 pt-2">`
        + `<div id="div_inventory_equipment_slots" class="flex-shrink-0"${equipDropAttrs}>${buildEquipmentColumn(caster, inv, canDrag, id)}</div>`
        + `</div>`;
}

/** On-Hand grid window body (own weight/GP banner; no inline containers). */
export function renderOnHandWindow(caster: CachedCaster, _casterId: string, viewerId = ''): string {
    const id = ecId(caster);
    if (!id) return '';
    const inv = sortedInventory(caster);
    const canDrag = invCanDrag(caster, viewerId);
    const rootId = 'div_onhandwin_encounter_creature_' + id;
    // hideTitle: the window titlebar already says "On Hand"; keep only the weight/GP chips in the banner.
    return `<div class="ft-pc-inventory ft-secwin ft-secwin-onhand px-2 pt-2">`
        + buildOnHandColumn(caster, inv, canDrag, rootId, { withContainers: false, hideTitle: true })
        + `</div>`;
}

/** One bag's body for a fullscreen bag window: the single equipped-container card. Returns '' when the
 *  container is gone (unequipped / dropped / not yours) so the manager can auto-close a stale window. */
export function renderBag(caster: CachedCaster, containerId: string, viewerId = ''): string {
    const id = ecId(caster);
    if (!id || !containerId) return '';
    const inv = inventoryRows(caster.inventory);
    const c = equippedContainers(inv).find((e) => String(ftInventoryField(e, 'inventory_id') ?? '') === containerId);
    if (!c) return '';
    const canDrag = invCanDrag(caster, viewerId);
    // The bag name rides on data-ft-charname so the window TITLEBAR shows it; hideName drops the redundant
    // name line inside the container card (the "Pouch … Pouch" repeat).
    const bagName = escapeHtml(decodeHtmlEntitiesForPlainText(String(ftInventoryField(c, 'inventory_name') ?? 'Bag')));
    return `<div class="ft-pc-inventory ft-bagwin-inv px-2 pt-2" data-ft-charname="${bagName}">${buildOneContainer(caster, c, canDrag, id, true)}</div>`;
}
import { decodeHtmlEntitiesForPlainText } from '../../shared/decode-html-entities';
